import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
    Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button, Divider, Flex, Icon, SimpleGrid, Text, useColorModeValue,
} from "@chakra-ui/react";
import { MdQrCode, MdPlayArrow, MdMiscellaneousServices } from "react-icons/md";
import Card from "horizon/components/card/Card";
import QRCodeSectionCard from "./QRCodeSectionCard";
import QRCodeResultSummary from "./QRCodeResultSummary";

/**
 * Builds the stats array for the services section card.
 * @param {object} services - The services result object from the API.
 * @returns {Array<object>} Stats rows for QRCodeSectionCard.
 */
function buildServiceStats(services) {
    const stats = [
        { label: "Total services", value: services.total },
        { label: "Missing QR", value: services.missing_qr, chipColor: "warning" },
        { label: "Generated", value: services.generated, chipColor: "success" },
    ];
    if (services.errors > 0) {
        stats.push({ label: "Errors", value: services.errors, chipColor: "error" });
    }
    return stats;
}

/**
 * Displays a toast notification based on generation results.
 * @param {number} totalGenerated - Number of QR codes generated.
 * @param {number} totalErrors - Number of errors during generation.
 */
function showResultToast(totalGenerated, totalErrors) {
    if (totalGenerated > 0) {
        toast.success(`${totalGenerated} QR code(s) being generated`);
    } else if (totalErrors > 0) {
        toast.error(`${totalErrors} error(s) during generation`);
    } else {
        toast("No missing QR codes");
    }
}

/** Renders the services section card for missing QR results. @param {Object} props @param {Object} props.result - API result */
function MissingQRSectionCards({ result }) {
    return (
        <SimpleGrid columns={{ base: 1 }} spacing="14px">
            <QRCodeSectionCard icon={MdMiscellaneousServices} iconColor="purple.500" title="Services"
                accent="purple.400" stats={buildServiceStats(result.services)} errorMessage={result.services.error_message} />
        </SimpleGrid>
    );
}

/** Renders success or info alerts based on generation totals. @param {Object} props */
function MissingQRAlerts({ totalGenerated, totalErrors }) {
    if (totalGenerated > 0) {
        return (
            <Alert status="success" borderRadius="10px" mt="14px" alignItems="flex-start">
                <AlertIcon />
                <Box>
                    <AlertTitle fontSize="sm">Generation in progress!</AlertTitle>
                    <AlertDescription fontSize="xs">{totalGenerated} Celery task(s) dispatched to generate missing QR codes in the background.</AlertDescription>
                </Box>
            </Alert>
        );
    }
    if (totalErrors === 0) {
        return (
            <Alert status="info" borderRadius="10px" mt="14px" alignItems="flex-start">
                <AlertIcon />
                <Box>
                    <AlertTitle fontSize="sm">No missing QR codes</AlertTitle>
                    <AlertDescription fontSize="xs">All services already have their QR codes.</AlertDescription>
                </Box>
            </Alert>
        );
    }
    return null;
}

/** Renders the result panel after a missing QR code scan. @param {object} props @param {object} props.result - API result with services data. */
function MissingQRResultPanel({ result }) {
    const bg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    const totalMissing = result.services.missing_qr || 0;
    const totalGenerated = result.services.generated || 0;
    const totalErrors = result.services.errors || 0;
    return (
        <Box p="16px" bg={bg} borderRadius="12px">
            <Text fontSize="sm" fontWeight="700" color={titleColor} mb="14px">Scan Results</Text>
            <QRCodeResultSummary totalMissing={totalMissing} totalSuccess={totalGenerated} successLabel="Generated" totalErrors={totalErrors} />
            <MissingQRSectionCards result={result} />
            <MissingQRAlerts totalGenerated={totalGenerated} totalErrors={totalErrors} />
        </Box>
    );
}

/**
 * Returns the error fallback result object used when the API call fails.
 * @param {string} errorMsg - The error message from the caught exception.
 * @returns {object} A result object with zeroed stats and the error message.
 */
function buildErrorResult(errorMsg) {
    return {
        services: { total: 0, missing_qr: 0, generated: 0, errors: 1, error_message: errorMsg },
    };
}

/**
 * Calls the API to generate missing QR codes, updates state and shows a toast.
 * @param {Function} setLoading - Loading state setter
 * @param {Function} setResult - Result state setter
 * @returns {Promise<void>}
 */
async function executeGenerateMissingQR(setLoading, setResult) {
    setLoading(true);
    setResult(null);
    try {
        const response = await axios.post('/admin/tools/generate_missing_qr_codes');
        const qrGeneration = response.data.qr_codes_generation;
        setResult(qrGeneration);
        showResultToast(qrGeneration.services.generated || 0, qrGeneration.services.errors || 0);
    } catch (error) {
        setResult(buildErrorResult(error.response?.data || error.message));
        toast.error("Error generating QR codes");
    } finally {
        setLoading(false);
    }
}

/**
 * Header section for the generate missing QR card.
 * @returns {React.ReactElement}
 */
function GenerateMissingQRHeader() {
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Box>
            <Flex align="center" gap="12px" mb="12px">
                <Icon as={MdQrCode} w="28px" h="28px" color="blue.400" />
                <Box>
                    <Text fontSize="lg" fontWeight="700" color={titleColor}>Generate Missing QR Codes</Text>
                    <Text fontSize="xs" color={subColor}>Generates QR codes for all services that do not have one</Text>
                </Box>
            </Flex>
            <Divider mb="14px" />
        </Box>
    );
}

/**
 * Body section with alert, button, and result panel.
 * @param {Object} props
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onGenerate - Generate handler
 * @param {Object|null} props.result - Generation result
 * @returns {React.ReactElement}
 */
function GenerateMissingQRBody({ loading, onGenerate, result }) {
    return (
        <Box>
            <Alert status="info" borderRadius="12px" mb="16px" alignItems="flex-start">
                <AlertIcon />
                <Box>
                    <AlertTitle fontSize="sm">Maintenance Tool</AlertTitle>
                    <AlertDescription fontSize="xs">Scans all non-test services to detect and generate missing QR codes. Generations are performed asynchronously via Celery.</AlertDescription>
                </Box>
            </Alert>
            <Button w="100%" colorScheme="blue" leftIcon={<Icon as={MdPlayArrow} />} onClick={onGenerate}
                isLoading={loading} loadingText="Scanning..." mb="16px">
                Start Generation
            </Button>
            {result && <MissingQRResultPanel result={result} />}
        </Box>
    );
}

/**
 * GenerateMissingQRCodes - Admin maintenance tool that scans all services
 * to detect and generate missing QR codes via Celery tasks.
 *
 * @returns {React.ReactElement} The generate missing QR codes card.
 */
function GenerateMissingQRCodes() {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    return (
        <Card mb="20px">
            <GenerateMissingQRHeader />
            <GenerateMissingQRBody loading={loading}
                onGenerate={() => executeGenerateMissingQR(setLoading, setResult)} result={result} />
        </Card>
    );
}

export default GenerateMissingQRCodes;
